/* eslint-disable padding-line-between-statements */
import React, { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useModal } from '@jutro/components';
import moment from 'moment';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { GatewayEndorsementService as LoadSaveService } from 'twia-capability-gateway-policychange';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import TWIADateFormatUtil from 'ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil.js';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import TWIAWizardHelper from 'producer-engage/src/overrides/modules-react/twia-portals-wizard-components-ui/Wizard/components/util/TWIAWizardHelper.js';
import metadata from './PolicyChangeDetails.metadata.json5';
import messages from './PolicyChangeDetails.messages.js';
import styles from './PolicyChangeDetails.module.scss'


function TFPAPolicyDetailsPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext)
    const { wizardData: submissionVM, updateWizardData, id } = props;
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { showAlert } = useModal();
    const { onValidate, isComponentValid, initialValidation } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const { refreshData } = useDataRefresh();
    const [showMinDateMessage, setShowMinDateMessage] = useState(false);
    const minimumEffectiveDate = _.get(submissionVM, `baseData.minimumEffectiveDate.value`);
    const formattedMinEffectiveDate = TWIADateFormatUtil.formatDate(minimumEffectiveDate);
    const intEffectiveDate = _.get(submissionVM, `baseData.initialEffectiveDate.value`);
    const today = LocalDateUtil.today();
    const isMinDateValid = moment(intEffectiveDate).isSameOrAfter(today);
    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate.value`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate.value`);

    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            const todayDate = LocalDateUtil.today();
            const isMinimumDateValid = moment(value).isSameOrAfter(todayDate);
            if (path === 'baseData.initialEffectiveDate') {
                _.set(submissionVM, 'baseData.effectiveDate', value);
                setShowMinDateMessage(!isMinimumDateValid);
            }
            updateWizardData(submissionVM);
            refreshData();
        },
        [refreshData, submissionVM, updateWizardData]
    );

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            setShowErrors(true)

            return false
        }
        // DE17700
        try {
            _.unset(submissionVM.value, 'bindData_tfpa');
            _.set(submissionVM, 'baseData.policyAddress.mustStandardize_Ext', false)
            // _.set(submissionVM, 'lobData.homeowners_tfpa.coverables', undefined)  // DE17708
            // _.set(submissionVM, 'lobData.homeowners_tfpa.coverables_Ext', undefined)

            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.unset(newSubmissionVM.value, 'bindData_TFPA');
            submissionVM.value = await LoadSaveService.saveEndorsement(
                newSubmissionVM.value,
                authHeader
            )

            return submissionVM
        } catch (error) {
            ErrorPopupUtil.showSaveErrorPopup(showAlert, false);

            return false;
        }
    }, [isComponentValid, submissionVM, viewModelService, authHeader, showAlert]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        proposedPolicyPeriod: {
            value: formatPolicyPeriod(periodStartDate, periodEndDate)
        },
        minDateMsg: {
            value: `Minimum date is ${formattedMinEffectiveDate}`,
            visible: showMinDateMessage,
        },
        dateOfBirth: {
            editable: false 
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    }

    const skip = useCallback(async () => {
        if (!isMinDateValid) {
            return false; // DE17904
        }

        return TWIAWizardHelper.skipStep(submissionVM, initialValidation);
    }, [isMinDateValid, initialValidation, submissionVM]); 

    return (

        <WizardPage
            id={id}
            onNext={onNext}
            skipWhen={skip}
            disableNext={!isMinDateValid || !submissionVM.baseData.initialEffectiveDate.aspects.valid}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                showErrors={showErrors}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onValueChange={writeValue}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

TFPAPolicyDetailsPage.propTypes = wizardProps;
export default TFPAPolicyDetailsPage;
